import { attr, FASTElement, html, Updates } from "@microsoft/fast-element";
import {
    autoPlacement,
    autoUpdate,
    computePosition,
    flip as flipMiddleware,
    offset as offsetMiddleware,
    size,
    type Middleware,
    type Placement
} from "@floating-ui/dom";
import { jsonConverter } from "../../value-converters/json-value-converter";
import { popperStyles } from "./popper.styles";
import { NatGenDesignSystem } from "../../natgen-design-system";

export type PopperPlacement = Placement | "auto";

/**
 * @element natgen-popper
 */
export class NatGenPopper extends FASTElement {
    protected target: HTMLElement | null = null;
    private cleanupAutoUpdate: (() => void) | null = null;
    private readonly boundToggle = (e: MouseEvent) => this.toggle(e);
    private readonly boundDocumentClick = (e: MouseEvent) => this.onDocumentClick(e);

    @attr
    selector: string;
    selectorChanged(oldValue: string, newValue: string): void {
        if (newValue !== oldValue)
            Updates.enqueue(() => this.updateTargetFromSelector());
    }

    @attr({ mode: 'boolean' })
    show: boolean = false;
    showChanged(): void {
        this.syncAutoUpdate();

        if (this.show)
            this.update();
    }

    @attr
    event: string = 'click';
    eventChanged(oldValue: string, newValue: string): void {
        if (!this.target)
            return;

        this.target.removeEventListener(oldValue, this.boundToggle, true);
        this.target.addEventListener(newValue, this.boundToggle, { capture: true });
    }

    @attr
    placement: PopperPlacement = 'bottom';
    placementChanged(): void {
        this.update();
    }

    @attr({ converter: jsonConverter })
    offset: [number, number];
    offsetChanged(): void {
        this.update();
    }

    @attr({ mode: 'boolean' })
    flip: boolean = false;
    flipChanged(): void {
        this.update();
    }

    @attr({ mode: 'boolean', attribute: 'match-target-width' })
    matchTargetWidth: boolean = false;
    matchTargetWidthChanged(): void {
        this.update();
    }

    protected buildMiddleware(): Middleware[] {
        const [crossAxisOffset, mainAxisOffset] = this.offset ?? [0, 8];

        const middleware: Middleware[] = [
            offsetMiddleware({
                mainAxis: mainAxisOffset,
                crossAxis: crossAxisOffset
            })
        ];

        if (this.placement === 'auto')
            middleware.push(autoPlacement());

        if (this.flip)
            middleware.push(flipMiddleware());

        if (this.matchTargetWidth) {
            middleware.push(size({
                apply: ({ rects, elements }) => {
                    elements.floating.style.width = `${rects.reference.width}px`;
                }
            }));
        }

        return middleware;
    }

    protected update(): void {
        if (!this.target || !this.isConnected || !this.show)
            return;

        computePosition(this.target, this, {
            placement: this.placement === 'auto' ? 'bottom' : this.placement,
            strategy: 'fixed',
            middleware: this.buildMiddleware()
        }).then(({ x, y }) => {
            this.style.position = 'fixed';
            this.style.left = `${x}px`;
            this.style.top = `${y}px`;
        });
    }

    toggle(e: MouseEvent = null) {
        this.show = !this.show;
        Updates.enqueue(() => {
            this.update();
            this?.focus({ preventScroll: true });
        });
        e?.stopPropagation();
    }

    protected onDocumentClick(e: MouseEvent) {
        if(this.show && this.target && !e.composedPath().includes(this) && !e.composedPath().includes(this.target))
            this.show = false;
    }

    connectedCallback() {
        super.connectedCallback();
        this.updateTargetFromSelector();
        document.addEventListener('click', this.boundDocumentClick);
        this.syncAutoUpdate();
        this.update();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.boundDocumentClick);
        this.cleanupAutoUpdate?.();
        this.cleanupAutoUpdate = null;

        if (this.target)
            this.target.removeEventListener(this.event, this.boundToggle, true);

        this.target = null;
    }

    private updateTargetFromSelector(): void {
        const root = this.getRootNode() as ParentNode;
        const newTarget = root?.querySelector?.(this.selector);

        if (!(newTarget instanceof HTMLElement))
            return;

        if (this.target === newTarget)
            return;

        if (this.target)
            this.target.removeEventListener(this.event, this.boundToggle, true);

        this.target = newTarget;
        this.target.addEventListener(this.event, this.boundToggle, { capture: true });
        this.syncAutoUpdate();
        this.update();
    }

    private syncAutoUpdate(): void {
        this.cleanupAutoUpdate?.();
        this.cleanupAutoUpdate = null;

        if (this.show && this.target && this.isConnected) {
            this.cleanupAutoUpdate = autoUpdate(this.target, this, () => {
                this.update();
            });
        }
    }
}

export const natGenPopperDefinition = NatGenPopper.compose({
    name: `${NatGenDesignSystem.prefix}-popper`,
    template: html`<slot></slot>`,
    styles: popperStyles
});
